/**
 * @license
 * Copyright 2024 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { Graph as GraphEditor } from "../editor/graph.js";
import { createLoader } from "../loader/index.js";
import { getGraphUrl, getGraphUrlComponents } from "../loader/loader.js";
import { graphUrlLike } from "../utils/graph-url-like.js";
import { hash } from "../utils/hash.js";
import { SnapshotUpdater } from "../utils/snapshot-updater.js";
import { UpdateEvent } from "./graph/event.js";
import { createBuiltInKit } from "./graph/kits.js";
import { MutableGraphImpl } from "./graph/mutable-graph.js";
import { filterEmptyValues } from "./utils.js";
export { contextFromMutableGraph, GraphStore, makeTerribleOptions };
function contextFromMutableGraph(mutable) {
    const store = mutable.store;
    return {
        kits: [...store.kits],
        loader: store.loader,
        sandbox: store.sandbox,
        graphStore: store,
        outerGraph: mutable.graph,
    };
}
// TODO: Deprecate and remove.
function makeTerribleOptions(options = {}) {
    return {
        kits: options.kits || [],
        sandbox: options.sandbox || {
            runModule() {
                throw new Error("Non-existent sandbox: Terrible Options were used.");
            },
        },
        loader: createLoader(),
        fileSystem: {
            read() {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
            write() {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
            query() {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
            close: function () {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
            createRunFileSystem: function () {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
            createModuleFileSystem: function () {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
            env: function () {
                throw new Error("Non-existent filesystem: Terrible Options were used.");
            },
        },
    };
}
class GraphStore extends EventTarget {
    kits;
    sandbox;
    loader;
    fileSystem;
    #legacyKits;
    #mainGraphIds = new Map();
    #mutables = new Map();
    #dependencies = new Map();
    constructor(args) {
        super();
        this.kits = args.kits;
        this.sandbox = args.sandbox;
        this.loader = args.loader;
        this.fileSystem = args.fileSystem;
        this.#legacyKits = this.#populateLegacyKits(args.kits);
    }
    getEntryByDescriptor(descriptor, graphId) {
        const getting = this.getOrAdd(descriptor, false);
        if (!getting.success) {
            return;
        }
        const mutable = getting.result;
        const mainGraphMetadata = filterEmptyValues({
            title: descriptor.title,
            description: descriptor.description,
            icon: descriptor.metadata?.icon,
            url: descriptor.url,
            tags: descriptor.metadata?.tags,
            help: descriptor.metadata?.help,
            id: mutable.id,
        });
        let metadata;
        if (graphId) {
            metadata = entryFromExport(mutable, `#${graphId}`, mutable.id);
        }
        else {
            metadata = mainGraphMetadata;
        }
        return {
            updating: false,
            mainGraph: mainGraphMetadata,
            ...metadata,
        };
    }
    mainGraphs() {
        const graphs = [...this.#mutables.entries()].map(([mainGraphId, snapshot]) => {
            const current = snapshot.current();
            const updating = snapshot.updating();
            const descriptor = current.graph;
            const mainGraphMetadata = filterEmptyValues({
                title: descriptor.title,
                description: descriptor.description,
                icon: descriptor.metadata?.icon,
                url: descriptor.url,
                tags: descriptor.metadata?.tags,
                help: descriptor.metadata?.help,
                id: mainGraphId,
            });
            return {
                ...mainGraphMetadata,
                ...getExports(current),
                updating,
            };
        });
        return graphs;
    }
    graphs() {
        const graphs = [...this.#mutables.entries()]
            .flatMap(([mainGraphId, snapshot]) => {
            const mutable = snapshot.current();
            const descriptor = mutable.graph;
            const mainGraphMetadata = filterEmptyValues({
                title: descriptor.title,
                description: descriptor.description,
                icon: descriptor.metadata?.icon,
                url: descriptor.url,
                tags: descriptor.metadata?.tags,
                help: descriptor.metadata?.help,
                id: mainGraphId,
            });
            const exports = [];
            if (descriptor.exports) {
                for (const e of descriptor.exports) {
                    const metadata = entryFromExport(mutable, e, mainGraphId);
                    exports.push({
                        mainGraph: mainGraphMetadata,
                        updating: false,
                        ...metadata,
                    });
                }
            }
            else {
                exports.push({
                    updating: false,
                    mainGraph: mutable.legacyKitMetadata || mainGraphMetadata,
                    ...mainGraphMetadata,
                });
            }
            return exports;
        })
            .filter(Boolean);
        return [...this.#legacyKits, ...graphs];
    }
    async load(path, context) {
        // Add loading graph as a dependency.
        const dependencies = [];
        if (context) {
            const url = context.outerGraph?.url;
            if (url) {
                const outerMutable = this.addByURL(url, [], context);
                dependencies.push(outerMutable.mutable.id);
            }
        }
        const result = this.addByURL(path, dependencies, context);
        try {
            const mutable = await this.getLatest(result.mutable);
            return {
                success: true,
                graph: mutable.graph,
                subGraphId: result.graphId,
                moduleId: result.moduleId,
            };
        }
        catch (e) {
            return {
                success: false,
                error: e.message,
            };
        }
    }
    #populateLegacyKits(kits) {
        kits = [...kits, createBuiltInKit()];
        const all = kits.flatMap((kit) => Object.entries(kit.handlers).map(([type, handler]) => {
            let metadata = "metadata" in handler ? handler.metadata || {} : {};
            const mainGraphTags = [...(kit.tags || [])];
            if (metadata.deprecated) {
                mainGraphTags.push("deprecated");
                metadata = { ...metadata };
                delete metadata.deprecated;
            }
            const tags = [...(metadata.tags || []), "component"];
            return [
                type,
                {
                    url: type,
                    mainGraph: filterEmptyValues({
                        title: kit.title,
                        description: kit.description,
                        tags: mainGraphTags,
                    }),
                    ...metadata,
                    tags,
                },
            ];
        }));
        return Object.values(all.reduce((collated, [type, info]) => {
            // Intentionally do the reverse of what goes on
            // in `handlersFromKits`: last info wins,
            // because here, we're collecting info, rather
            // than handlers and the last info is the one
            // that typically has the right stuff.
            return { ...collated, [type]: info };
        }, {}));
    }
    registerKit(kit, dependences) {
        Object.keys(kit.handlers).forEach((type) => {
            if (graphUrlLike(type)) {
                const mutable = this.addByURL(type, dependences, {}).mutable;
                mutable.legacyKitMetadata = filterEmptyValues({
                    url: kit.url,
                    title: kit.title,
                    description: kit.description,
                    tags: kit.tags,
                    id: mutable.id,
                });
            }
            else {
                throw new Error(`The type "${type}" is not Graph URL-like, unable to add this kit`);
            }
        });
    }
    addByDescriptor(graph) {
        const getting = this.getOrAdd(graph, true);
        if (!getting.success) {
            return getting;
        }
        return { success: true, result: getting.result.id };
    }
    getByDescriptor(graph) {
        const getting = this.getOrAdd(graph, false);
        if (!getting.success) {
            return getting;
        }
        return { success: true, result: getting.result.id };
    }
    editByDescriptor(graph, options = {}) {
        const result = this.getOrAdd(graph, true);
        if (!result.success) {
            console.error(`Failed to edityByDescriptor: ${result.error}`);
            return undefined;
        }
        return this.edit(result.result.id, options);
    }
    edit(id, options = {}) {
        const mutable = this.get(id);
        if (!mutable)
            return undefined;
        return new GraphEditor(mutable, options);
    }
    inspect(id, graphId) {
        const mutable = this.get(id);
        if (!mutable)
            return undefined;
        return mutable.graphs.get(graphId);
    }
    inspectSnapshot(graph, graphId) {
        const immutable = this.#snapshotFromGraphDescriptor(graph).current();
        return immutable.graphs.get(graphId);
    }
    addByURL(path, dependencies, context = {}) {
        const { mainGraphUrl, graphId, moduleId } = getGraphUrlComponents(getGraphUrl(path, context));
        const id = this.#mainGraphIds.get(mainGraphUrl);
        if (id) {
            this.#addDependencies(id, dependencies);
            const snapshot = this.#mutables.get(id);
            return {
                mutable: snapshot.current(),
                graphId,
                moduleId,
                updating: snapshot.updating(),
            };
        }
        const snapshot = this.#snapshotFromUrl(mainGraphUrl, context);
        const mutable = snapshot.current();
        this.#mutables.set(mutable.id, snapshot);
        this.#mainGraphIds.set(mainGraphUrl, mutable.id);
        this.#addDependencies(mutable.id, dependencies);
        return {
            mutable,
            graphId,
            moduleId,
            updating: snapshot.updating(),
        };
    }
    async getLatest(mutable) {
        const snapshot = this.#mutables.get(mutable.id);
        if (!snapshot) {
            return mutable;
        }
        return snapshot.latest();
    }
    #addDependencies(id, dependencies) {
        if (!dependencies.length)
            return;
        let deps = this.#dependencies.get(id);
        if (!deps) {
            deps = new Set();
            this.#dependencies.set(id, deps);
        }
        dependencies.forEach((dependency) => {
            deps.add(dependency);
        });
    }
    getOrAdd(graph, sameCheck) {
        let url = graph.url;
        let graphHash = null;
        if (!url) {
            graphHash = hash(graph);
            url = `hash:${graphHash}`;
        }
        // Find graph by URL.
        const id = this.#mainGraphIds.get(url);
        if (id) {
            const existing = this.#mutables.get(id)?.current();
            if (!existing) {
                return error(`Integrity error: main graph "${id}" not found in store.`);
            }
            const same = !sameCheck ||
                graphHash !== null ||
                hash(existing.graph) === hash(graph);
            if (!same) {
                // When not the same, rebuild the graph on the MutableGraphImpl.
                existing.rebuild(graph);
            }
            return { success: true, result: existing };
        }
        else {
            // Brand new graph
            const snapshot = this.#snapshotFromGraphDescriptor(graph);
            const mutable = snapshot.current();
            this.#mutables.set(mutable.id, snapshot);
            this.#mainGraphIds.set(url, mutable.id);
            return { success: true, result: mutable };
        }
    }
    /**
     * Creates a snapshot of a MutableGraph that is based on the
     * GraphDescriptor instance.
     *
     * This is basically a constant -- calling `refresh` doesn't do anything,
     * and `latest` is immediately resolved to the same value as `current`.
     *
     * @param graph
     * @returns
     */
    #snapshotFromGraphDescriptor(graph) {
        const mutable = new MutableGraphImpl(graph, this);
        return new SnapshotUpdater({
            initial() {
                return mutable;
            },
            latest() {
                return Promise.resolve(mutable);
            },
        });
    }
    #snapshotFromUrl(url, options) {
        const mutable = new MutableGraphImpl(emptyGraph(), this);
        let graphId = "";
        return new SnapshotUpdater({
            initial: () => mutable,
            latest: async () => {
                const loader = this.loader;
                if (!loader) {
                    throw new Error(`Unable to load "${url}": no loader provided`);
                }
                const loading = await loader.load(url, options);
                if (!loading.success) {
                    throw new Error(loading.error);
                }
                mutable.rebuild(loading.graph);
                graphId = loading.subGraphId || "";
                return mutable;
            },
            updated: () => {
                this.dispatchEvent(new UpdateEvent(mutable.id, graphId, "", [
                    ...(this.#dependencies.get(mutable.id) || []),
                ]));
            },
        });
    }
    get(id) {
        return this.#mutables.get(id)?.current();
    }
}
function error(message) {
    return {
        success: false,
        error: message,
    };
}
function emptyGraph() {
    return {
        edges: [],
        nodes: [],
    };
}
const MODULE_EXPORT_PREFIX = "#module:";
function entryFromExport(mutable, id, mainGraphId) {
    const graph = mutable.graph;
    const url = `${graph.url}${id}`;
    const { current, updating } = mutable.describe.getByType(url);
    const { title, description, metadata: { icon, help, tags = [], order } = {}, } = updating ? {} : current || {};
    if (id.startsWith(MODULE_EXPORT_PREFIX)) {
        const moduleId = id.slice(MODULE_EXPORT_PREFIX.length);
        const module = graph.modules?.[moduleId];
        if (!module)
            return null;
        return filterEmptyValues({
            title: title ?? module.metadata?.title,
            description: description ?? module.metadata?.description,
            icon: icon ?? module.metadata?.icon,
            url,
            tags: ["component", ...tags],
            help: help ?? module.metadata?.help,
            order,
            id: mainGraphId,
            updating,
        });
    }
    else {
        const graphId = id.slice(1);
        const descriptor = graphId ? graph.graphs?.[graphId] : graph;
        if (!descriptor)
            return null;
        return filterEmptyValues({
            title: title ?? descriptor.title,
            description: description ?? descriptor.description,
            icon: icon ?? descriptor.metadata?.icon,
            url,
            tags: ["component", ...tags],
            help: help ?? descriptor.metadata?.help,
            order,
            id: mainGraphId,
            updating,
        });
    }
}
function getExports(mutable) {
    const result = [];
    const tags = new Set();
    const { exports = [] } = mutable.graph;
    for (const id of exports) {
        const entry = entryFromExport(mutable, id, mutable.id);
        if (!entry)
            continue;
        result.push(entry);
        (entry.tags || []).forEach((tag) => tags.add(tag));
    }
    return { exports: result, exportTags: Array.from(tags.values()) };
}
//# sourceMappingURL=graph-store.js.map